import { useCallback, useEffect, useState } from "react";
import { ErrorCodeType } from "application/models/IError";
import { useAppInsights } from "Infrastructure/repositories/appInsights/AppInsights";
import { EventRepository } from "Infrastructure/repositories/api/EventRepository";
import { IEventRepository } from "modules/events/domain/interfaces/IEventRepository";
import { IEvent } from "modules/events/domain/models/IEvent";
import { GetEventsUseCase } from "modules/events/application/useCases/GetEventsUseCase";
import { LoginEventsUseCase } from "modules/events/application/useCases/LoginEventsUseCase";
import { ILoginEventParams, ILoginEventResponse } from "modules/events/domain/models/ILoginEvent";
import { GetError } from "utils/GetError";

export const useEvents = (
	localClienteId?: string,
	enabled: boolean = true
) => {
	const [events, setEvents] = useState<IEvent[]>([]);
	const [loading, setLoading] = useState(false);
	const [loginLoading, setLoginLoading] = useState(false);
	const repository: IEventRepository = EventRepository();
	const { addLog } = useAppInsights();

	const fetchEvents = useCallback(async () => {
		if (!enabled || !localClienteId) {
			setEvents([]);
			return;
		}

		setLoading(true);
		try {
			const response = await GetEventsUseCase(repository, localClienteId);
			setEvents(response ?? []);
		} catch (error) {
			addLog("useEvents_fetchEvents_error", { error, localClienteId });
			GetError(
				error,
				"useEvents",
				"useEvents",
				"Falha ao carregar eventos",
				ErrorCodeType.GENERAL
			);
		} finally {
			setLoading(false);
		}
	}, [enabled, localClienteId]);

	const loginEvent = useCallback(
		async (
			params: Omit<ILoginEventParams, "localClienteId" | "dataEquipamentoUTC" | "serial" | "version"> & {
				dataEquipamentoUTC?: string;
				serial?: string;
				version?: string;
			}
		): Promise<ILoginEventResponse | undefined> => {
			if (!localClienteId) {
				return undefined;
			}

			const payload: ILoginEventParams = {
				localClienteId,
				eventId: params.eventId,
				operadorId: params.operadorId,
				dataEquipamentoUTC: params.dataEquipamentoUTC ?? new Date().toISOString(),
				serial: params.serial ?? "",
				version: params.version ?? "",
			};

			setLoginLoading(true);
			try {
				const response = await LoginEventsUseCase(repository, payload);

				return response;
			} catch (error) {
				addLog("useEvents_loginEvent_error", {
					error,
					localClienteId,
					eventId: params.eventId,
					operadorId: params.operadorId,
				});
				GetError(
					error,
					"useEvents",
					"useEvents",
					"Falha ao logar no evento",
					ErrorCodeType.GENERAL
				);
				throw error;
			} finally {
				setLoginLoading(false);
			}
		},
		[localClienteId, repository]
	);

	useEffect(() => {
		fetchEvents();
	}, [fetchEvents]);

	return { events, loading, loginLoading, refresh: fetchEvents, loginEvent };
};
